// hooks/useStaffForm.js
import { useState, useCallback } from "react";

const initialFormData = {
  user: {
    name: "",
    email: "",
    firstname: "",
    lastname: "",
    job_title: "",
    profile_data: {
      mobile_number: "",
      country_code: "+91",
      photo_id: null,
      profile_url: null,
      other_documents: [],
    },
  },
};

export const useStaffForm = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState({});

  // Update a specific field
  const updateField = useCallback((field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        user: {
          ...prev.user,
          [parent]: {
            ...prev.user[parent],
            [child]: value,
          },
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        user: {
          ...prev.user,
          [field]: value,
        },
      }));
    }

    // Clear error for this field
    setFormErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  // Add document to other_documents array
  const addDocument = useCallback((document) => {
    setFormData((prev) => ({
      ...prev,
      user: {
        ...prev.user,
        profile_data: {
          ...prev.user.profile_data,
          other_documents: [
            ...prev.user.profile_data.other_documents,
            document,
          ],
        },
      },
    }));
  }, []);

  // Remove document from other_documents array
  const removeDocument = useCallback((index) => {
    setFormData((prev) => ({
      ...prev,
      user: {
        ...prev.user,
        profile_data: {
          ...prev.user.profile_data,
          other_documents: prev.user.profile_data.other_documents.filter(
            (_, i) => i !== index
          ),
        },
      },
    }));
  }, []);

  // Update documents array
  const updateDocuments = useCallback((documents) => {
    setFormData((prev) => ({
      ...prev,
      user: {
        ...prev.user,
        profile_data: {
          ...prev.user.profile_data,
          other_documents: documents,
        },
      },
    }));
  }, []);

  // Validate form
  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.user.firstname?.trim()) {
      errors.firstname = "First name is required";
    }

    if (!formData.user.email?.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.user.email)) {
      errors.email = "Email is invalid";
    }

    if (!formData.user.profile_data.mobile_number?.trim()) {
      errors["profile_data.mobile_number"] = "Mobile number is required";
    } else if (!/^\d+$/.test(formData.user.profile_data.mobile_number)) {
      errors["profile_data.mobile_number"] =
        "Mobile number should contain only digits";
    }

    const validCountryCodes = ["+91", "+1", "+44", "+61"];
    if (!validCountryCodes.includes(formData.user.profile_data.country_code)) {
      errors["profile_data.country_code"] =
        "Please select a valid country code";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  // Reset form to initial state
  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setFormErrors({});
  }, []);

  // Set form data from existing staff member
  const setFormDataFromStaff = useCallback((staff) => {
    setFormData({
      user: {
        id: staff.user?.id || staff.id,
        name: staff.user?.name || "",
        email: staff.user?.email || "",
        firstname: staff.user?.name?.split(" ")[0] || "",
        lastname: (staff.user?.name?.split(" ").slice(1) || []).join(" ") || "",
        job_title:
          staff.user?.job_title || staff.user?.profile_data?.role_auth || "",
        profile_data: {
          country_code: staff.user?.profile_data?.country_code || "+91",
          mobile_number:
            staff.user?.profile_data?.mobile_number ||
            staff.user?.profile_data?.number ||
            "",
          photo_id: staff.user?.profile_data?.photo_id || null,
          profile_url: staff.user?.profile_data?.profile_url || null,
          other_documents: staff.user?.profile_data?.other_documents || [],
        },
      },
    });
    setFormErrors({});
  }, []);

  // Check if form is valid (basic validation)
  const isValid =
    formData.user.firstname?.trim() &&
    formData.user.email?.trim() &&
    formData.user.profile_data.mobile_number?.trim();

  return {
    formData,
    formErrors,
    updateField,
    addDocument,
    removeDocument,
    updateDocuments,
    validateForm,
    resetForm,
    setFormDataFromStaff,
    isValid,
  };
};
